import Text from '@ingka/text';
import Button from '@ingka/button';
import plusIcon from '@ingka/ssr-icon/paths/plus';
import {
  useAppSelector,
  useGetLotteriesQuery,
  useGetPermissionsQuery,
  useGetRetailUnitQuery,
  selectName,
} from '@lotta/self-service/lottery-fe/data-access-state';
import Loading, { LoadingBall } from '@ingka/loading';
import { MissingData, MissingDataSizes } from '@lotta/self-service/lottery-fe/ui-common';
import {
  useFormatWithLocale,
  useNavigateWithLocale,
} from '@lotta/self-service/lottery-fe/feature-hooks';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { LocaleParams } from '@lotta/self-service/lottery-fe/types-router';
import { getUserPermissions } from '@lotta/self-service/lottery-fe/util-utilities';
import Tooltip from '@ingka/tooltip';
import styles from './Home.module.scss';
import { LotteryTable } from './components/lottery-table/LotteryTable';

/* eslint-disable-next-line */
export type HomeComponentProps = {};

export const Home: React.FC<HomeComponentProps> = (props) => {
  const { t } = useTranslation();
  const { countryCode } = useParams<LocaleParams>() as LocaleParams;
  const { data, isLoading, error } = useGetLotteriesQuery(countryCode);
  const { data: retailUnitData, isLoading: isRetailUnitLoading } = useGetRetailUnitQuery({
    retailUnit: countryCode,
  });
  const navigate = useNavigateWithLocale();
  const userName = useAppSelector(selectName);
  const { numberWithLocale } = useFormatWithLocale();
  const { data: permissionData, isLoading: isPermissionsLoading } = useGetPermissionsQuery({});

  if (isLoading) {
    return (
      <Loading text={`${t('home-fetching-lotteries')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (error || !data) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  let userPermission: string[] = [];

  if (permissionData && !isPermissionsLoading) {
    userPermission = getUserPermissions(countryCode.toUpperCase(), permissionData.permissions);
  }

  let timeZone = '';

  if (retailUnitData && !isRetailUnitLoading) {
    timeZone = retailUnitData.retailUnit.timeZone;
  }

  const { lotteries } = data;
  const nbrLotteries = lotteries.length;

  const allowedGroups = ['SUPER_ADMIN', 'ADMIN', 'EDITOR'];

  const isCreateDisabled = !userPermission.some((role) => allowedGroups.includes(role));

  const createButton = (
    <Button
      small
      type="primary"
      ssrIcon={plusIcon}
      disabled={isCreateDisabled}
      iconPosition="leading"
      data-testid="create-lottery-button"
      onClick={() => {
        navigate('/create');
      }}
    >
      {t('general-create-lottery')}
    </Button>
  );

  return (
    <div className={styles['container']} data-testid="home-container">
      <Text headingSize="xl" className={styles['header']}>
        {t('home-welcome', { userName })}
      </Text>
      <div className={styles['subtitle-row']}>
        <Text headingSize="l" className={styles['subtitle']}>
          {nbrLotteries === 1
            ? t('home-available-lottery')
            : t('home-available-lotteries-nbr', { nbrLotteries: numberWithLocale(nbrLotteries) })}
        </Text>
        {isCreateDisabled ? (
          <Tooltip
            className="tooltip-demo__item"
            interactiveCustomTrigger
            tooltipText={t('general-insufficient-permissions')}
          >
            {createButton}
          </Tooltip>
        ) : (
          createButton
        )}
      </div>
      {lotteries.length ? (
        <LotteryTable lotteries={lotteries} timeZone={timeZone} />
      ) : (
        <MissingData text={t('home-no-available-lotteries')} size={MissingDataSizes.LARGE} />
      )}
    </div>
  );
};
